import { useEffect, useMemo, useRef } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "@/hooks/useServices";
import { useIndexingOperationsStore } from "@/stores/indexing-operations.store";
import { logger } from "@/lib/logger";
import { invalidateDocumentHtml } from "@/lib/documentHtmlQuery";
import { showErrorToast, showSuccessToast } from "@/components/custom-toasts/toasts";
import {
  claimIndexingOperation,
  hasHandledIndexingOperation,
} from "@/lib/indexingRunRegistry";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { canReindexItem } from "@/pages/workspace/utils/workspaceAccess";
import {
  isChatIndexableDocumentType,
  Item,
  ITEM_TYPE_DOCUMENT,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { DocumentIndexingView } from "@/pages/workspace/utils/indexingProgress";
import { restError } from "@/models/client/RestResponse";

const POLL_INTERVAL_MS = 2500;

/**
 * Tracks background indexing runs for the files views (LAR-252 async indexing).
 *
 * Watches two kinds of documents: those whose active revision is not indexed
 * (an earlier run failed or is still in flight — e.g. after a reload), and
 * those with an operation tracked in this session (a healthy document being
 * regenerated shows no revision change until it completes). A session tracks
 * the runs it starts and, on load, the runs the API reports as live, so a
 * rerun survives a reload and shows in another tab or for another user.
 * While a run is in flight the status endpoint is polled; when it reports
 * "succeeded" the hook
 * silently calls reindex once more — the machine version of the manual second
 * click — which lands on the API's instant heal path and finalises everything.
 * Finalisation needs write access, so viewers with read-only access get the
 * running spinner but never the auto-heal or its error toasts.
 *
 * Returns the documents whose status cell should show the indexing spinner,
 * with where each run is (LAR-486).
 */
export function useDocumentIndexingStatuses(
  workspaceId: string | undefined,
  items: Item[]
): { indexingDocuments: ReadonlyMap<string, DocumentIndexingView> } {
  const workspaceService = useItemService((services) => services.workspace);
  const queryClient = useQueryClient();
  const { workspace, myUser, isParentWorkspace } = useWorkspaceContext();
  const canFinalise =
    !!workspace && !!myUser && canReindexItem(workspace, myUser.id, isParentWorkspace);
  const startedOperations = useIndexingOperationsStore(
    (state) => state.operations
  );
  const clearOperation = useIndexingOperationsStore((state) => state.clear);
  const startOperation = useIndexingOperationsStore((state) => state.start);
  /** Operations whose heal errored: stop showing their cached "succeeded" as a spinner. */
  const failedHealsRef = useRef<Set<string>>(new Set());
  /** Operations already reported as stalled, so the warning is logged once per run. */
  const reportedStallsRef = useRef<Set<string>>(new Set());

  // A run this tab did not start (a rerun before a reload, from another tab or
  // by another user) on a document that is already indexed matches neither
  // rule below, so the row showed the old state and offered "Genkør" again.
  // The API lists the live runs; track them like the ones started here.
  const runningOperations = useQuery({
    queryKey: [PATH_KEYS.workspace, "runningIndexOperations", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const response = await workspaceService.getRunningIndexOperations(
        workspaceId!
      );
      if (response.success) return response.value;
      // An API from before the endpoint: there is nothing to pick up.
      if (response.status === 404) return [];
      throw restError(response);
    },
  });

  useEffect(() => {
    for (const { documentId, operationId } of runningOperations.data ?? []) {
      // Finished or reported already: tracking it again would spin forever.
      if (hasHandledIndexingOperation(operationId)) continue;
      if (startedOperations[documentId] === operationId) continue;
      startOperation(documentId, operationId);
    }
  }, [runningOperations.data, startedOperations, startOperation]);

  const candidateIds = useMemo(() => {
    const ids = new Set<string>(Object.keys(startedOperations));
    for (const item of items) {
      if (item.type !== ITEM_TYPE_DOCUMENT) continue;
      if (!isChatIndexableDocumentType(item.documentType)) continue;
      const activeRevision = item.revisions?.find(
        (r) => r.revisionVersion === item.activeRevision
      );
      if (activeRevision && activeRevision.isIndexed !== true) {
        ids.add(item.id);
      }
    }
    return [...ids];
  }, [items, startedOperations]);

  const statusQueries = useQueries({
    queries: candidateIds.map((documentId) => ({
      queryKey: [PATH_KEYS.workspace, "documentIndexStatus", workspaceId, documentId],
      enabled: !!workspaceId,
      queryFn: async () => {
        const response = await workspaceService.getDocumentIndexStatus(
          workspaceId!,
          documentId
        );
        if (response.success) return response.value;
        throw restError(response);
      },
      refetchInterval: (query: { state: { data?: { state?: string } } }) =>
        query.state.data?.state === "running" ? POLL_INTERVAL_MS : false,
    })),
  });

  const heal = useMutation({
    mutationKey: [PATH_KEYS.workspace, "healIndexedDocument"],
    mutationFn: async (params: { documentId: string; operationId: string }) => {
      // The operation id marks this as a finalisation: the API never starts a
      // new run for it, so concurrent heals from other viewers are no-ops.
      const response = await workspaceService.reindexDocument(
        workspaceId!,
        params.documentId,
        params.operationId
      );
      if (response.success) return response.value;
      throw restError(response);
    },
  });

  const statusSignature = statusQueries
    .map((q) => `${q.data?.state ?? ""}:${q.data?.operationId ?? ""}`)
    .join("|");

  useEffect(() => {
    // Finalisation calls reindex, which needs write access — read-only viewers
    // just watch the running spinner and never toast about runs they can't fix.
    if (!workspaceId || !canFinalise) return;

    candidateIds.forEach((documentId, index) => {
      const status = statusQueries[index]?.data;
      if (!status?.operationId) return;
      if (hasHandledIndexingOperation(status.operationId)) return;

      if (status.state === "succeeded") {
        const operationId = status.operationId;
        if (!claimIndexingOperation(operationId)) return;
        // mutateAsync, not mutate: per-call callbacks of mutate only fire for the
        // latest call, so a bulk rerun whose runs finish together would lose all but
        // the last one's bookkeeping and leave the rest spinning forever.
        void heal
          .mutateAsync({ documentId, operationId })
          .then((result) => {
            if (result.status === "indexing" && result.operationId) {
              // A live run came back instead of a completion — the heal
              // adopted a regeneration someone else just started (or an old
              // API started a fresh run). Track it, and refetch the status
              // so the cached "succeeded" stops blocking the poll loop.
              clearOperation(documentId);
              startOperation(documentId, result.operationId);
              queryClient.invalidateQueries({
                queryKey: [
                  PATH_KEYS.workspace,
                  "documentIndexStatus",
                  workspaceId,
                  documentId,
                ],
              });
              return;
            }
            clearOperation(documentId);
            queryClient.invalidateQueries({
              queryKey: [PATH_KEYS.workspace],
              exact: false,
            });
            // The run regenerated this document's HTML.
            if (workspaceId) {
              invalidateDocumentHtml(queryClient, workspaceId, documentId);
            }
            queryClient.invalidateQueries({
              queryKey: [PATH_KEYS.search, "tenant"],
              exact: false,
            });
            showSuccessToast("Kildeklargøring er fuldført.");
          })
          .catch((error: unknown) => {
            failedHealsRef.current.add(operationId);
            clearOperation(documentId);
            logger.error("Failed to finalise a completed indexing run", {
              operation: "healIndexedDocument",
              error,
              workspaceId,
              documentId,
              operationId,
            });
            showErrorToast(
              "Kildeklargøringen blev fuldført, men status kunne ikke opdateres. Genkør kildeklargøring."
            );
          });
      } else if (status.state === "failed") {
        if (!claimIndexingOperation(status.operationId)) return;
        clearOperation(documentId);
        logger.error("Background indexing run failed", {
          operation: "documentIndexStatus",
          error: new Error(status.error ?? "unknown"),
          workspaceId,
          documentId,
          operationId: status.operationId,
          phase: status.progress?.phase ?? undefined,
        });
        showErrorToast(
          "Kildeklargøring fejlede. Genkør kildeklargøring for at prøve igen."
        );
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.workspace],
          exact: false,
        });
      }
    });
    // The signature string keys this effect to actual status changes;
    // statusQueries itself is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateIds, statusSignature, workspaceId, canFinalise]);

  const stallSignature = statusQueries
    .map((q) => (q.data?.progress?.stalled ? (q.data.operationId ?? "") : ""))
    .join("|");

  useEffect(() => {
    // A stalled run's worker has stopped (LAR-486). The row says so; this
    // makes it visible to us too, for every viewer and once per run.
    candidateIds.forEach((documentId, index) => {
      const status = statusQueries[index]?.data;
      if (status?.state !== "running" || !status.progress?.stalled) return;
      if (!status.operationId) return;
      if (reportedStallsRef.current.has(status.operationId)) return;
      reportedStallsRef.current.add(status.operationId);
      logger.warn("Background indexing run stopped reporting", {
        operation: "documentIndexStatus",
        workspaceId,
        documentId,
        operationId: status.operationId,
        phase: status.progress.phase ?? undefined,
        heartbeatAt: status.progress.heartbeatAt ?? undefined,
      });
    });
    // Keyed on the stalled operations only, like the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateIds, stallSignature, workspaceId]);

  const indexingDocuments = useMemo(() => {
    const views = new Map<string, DocumentIndexingView>();
    candidateIds.forEach((documentId, index) => {
      const status = statusQueries[index]?.data;
      if (status?.state === "running") {
        views.set(documentId, {
          stage: "running",
          progress: status.progress ?? null,
        });
        return;
      }
      // "succeeded" spins only while this session can and will finalise it —
      // for read-only viewers, or after a failed heal, the revision-based
      // status is the honest one.
      if (
        status?.state === "succeeded" &&
        canFinalise &&
        !(status.operationId && failedHealsRef.current.has(status.operationId))
      ) {
        views.set(documentId, { stage: "finalising", progress: null });
        return;
      }
      // Operation accepted this session but the first status read is still in
      // flight — show the spinner rather than flashing the old state.
      if (documentId in startedOperations && status?.state === undefined) {
        views.set(documentId, { stage: "starting", progress: null });
      }
    });
    return views;
  }, [candidateIds, statusQueries, startedOperations, canFinalise]);

  return { indexingDocuments };
}
